import { Eye } from 'lucide-react';
import styled from 'styled-components';

import { Badge } from '../../../components/atoms/badge/badge';
import { IconButton } from '../../../components/atoms/icon-button/icon-button';
import { ClienteListItem } from '../../../types/cliente';
import { ClienteKycBadge } from './cliente-kyc-badge';
import { formatDocumento, formatTelefone } from './format';

interface Props {
  items: ClienteListItem[];
  onOpenDetail: (id: string) => void;
}

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.textMuted};
    background: ${({ theme }) => theme.colors.bgMuted};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    font-weight: 600;
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  th:last-child,
  td:last-child {
    text-align: right;
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  strong {
    font-weight: 600;
  }

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12.5px;
  }
`;

const SrOnly = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const fullName = (item: ClienteListItem): string =>
  `${item.nome ?? ''} ${item.sobrenome ?? ''}`.trim() || '—';

export const ClientesTable = ({ items, onOpenDetail }: Props) => (
  <Table>
    <SrOnly>Lista de clientes</SrOnly>
    <thead>
      <tr>
        <th scope="col">Cliente</th>
        <th scope="col">Documento</th>
        <th scope="col">Tipo</th>
        <th scope="col">Telefone</th>
        <th scope="col">KYC</th>
        <th scope="col">Ações</th>
      </tr>
    </thead>
    <tbody>
      {items.map((cliente) => (
        <tr key={cliente.id} data-testid={`cliente-row-${cliente.id}`}>
          <td>
            <NameCell>
              <strong>{fullName(cliente)}</strong>
              <small>{cliente.email}</small>
            </NameCell>
          </td>
          <td>{formatDocumento(cliente.documento)}</td>
          <td>
            {cliente.tipoPessoa ? (
              <Badge variant="info">{cliente.tipoPessoa}</Badge>
            ) : (
              <Badge variant="neutral">—</Badge>
            )}
          </td>
          <td>{formatTelefone(cliente.telefone)}</td>
          <td>
            <ClienteKycBadge
              status={cliente.statusKyc}
              description={cliente.statusKycDescricao}
            />
          </td>
          <td>
            <IconButton
              label={`Ver detalhes de ${fullName(cliente)}`}
              onClick={() => onOpenDetail(cliente.id)}
            >
              <Eye size={16} />
            </IconButton>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);
